// Utility functions for survey data parsing

export function getCol(row, keywords) {
  const k = keywords.map(x => x.toLowerCase());
  // Match against first line of column name only (before any newline/description)
  // This prevents e.g. "large language model" matching the Year column
  // whose description mentions LLMs
  for (const key of Object.keys(row)) {
    const firstLine = key.split('\n')[0].toLowerCase();
    if (k.some(kw => firstLine.includes(kw))) return row[key] || '';
  }
  return '';
}

export function parseMulti(val) {
  if (!val) return [];
  return val.split(';').map(s => s.trim()).filter(Boolean);
}

export function countValues(rows, keywords) {
  const counts = {};
  rows.forEach(r => {
    const val = getCol(r, keywords);
    parseMulti(val).forEach(v => {
      if (v) counts[v] = (counts[v] || 0) + 1;
    });
  });
  return counts;
}

export function topN(obj, n = 8) {
  return Object.entries(obj).sort((a, b) => b[1] - a[1]).slice(0, n);
}

export function avg(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

export function computeMetrics(rows) {
  const n = rows.length;
  const hasAI = rows.filter(r => getCol(r, ['currently offer ai']).toLowerCase() === 'yes').length;
  const hasKB = rows.filter(r => getCol(r, ['knowledge base/content']).toLowerCase() === 'yes').length;
  const hasOutcomes = rows.filter(r => ['yes', 'partially'].includes(getCol(r, ['track farmer-level']).toLowerCase())).length;
  const costTracked = rows.filter(r => ['yes', 'partially'].includes(getCol(r, ['costs of ai advisory']).toLowerCase())).length;
  const hasAPI = rows.filter(r => getCol(r, ['apis or integration']).toLowerCase() === 'yes').length;
  const assessQuality = rows.filter(r => getCol(r, ['assess the quality']).toLowerCase() === 'yes').length;

  // Farmers reached — prefer Kenya-specific numbers from messy free-text responses
  const parseNumWithSuffix = (str) => {
    const m = str.match(/([\d.]+)\s*(m|million|k|thousand)?/i);
    if (!m) return 0;
    let n = parseFloat(m[1]);
    if (m[2]) {
      const s = m[2].toLowerCase();
      if (s === 'm' || s === 'million') n *= 1000000;
      if (s === 'k' || s === 'thousand') n *= 1000;
    }
    return isNaN(n) ? 0 : Math.round(n);
  };

  const farmerNums = rows.map(r => {
    let raw = getCol(r, ['estimated number of farmers']);
    if (!raw) return 0;
    const cleaned = raw.replace(/,/g, '').trim();
    const lower = cleaned.toLowerCase();

    // 1. Prefer Kenya-specific number: "200,000 in Kenya" or "Kenya only 200000"
    const kenyaMatch = lower.match(/([\d.]+\s*(?:m|million|k|thousand)?)\s*(?:in\s+)?kenya/i)
      || lower.match(/kenya[^0-9]*([\d.]+\s*(?:m|million|k|thousand)?)/i);
    if (kenyaMatch) return parseNumWithSuffix(kenyaMatch[1]);

    // 2. Prefer "with AI" number: "not AI- 500k, with AI- 5k"
    const aiMatch = lower.match(/with\s+ai[^0-9]*([\d.]+\s*(?:m|million|k|thousand)?)/i);
    if (aiMatch) return parseNumWithSuffix(aiMatch[1]);

    // 3. If text says "globally" without Kenya number, skip it
    if (lower.includes('globally') && !lower.includes('kenya')) return 0;

    // 4. If text says "small" or "validation stage", skip
    if (lower.includes('small') || lower.includes('validation')) return 0;

    // 5. Fallback: take first number found
    return parseNumWithSuffix(cleaned);
  }).filter(v => v > 0);
  const totalFarmers = farmerNums.reduce((a, b) => a + b, 0);

  return { n, hasAI, hasKB, hasOutcomes, costTracked, hasAPI, assessQuality, totalFarmers };
}

export function computeRatings(rows) {
  const dimensions = [
    { label: 'Relevance of advisory content', key: 'relevance' },
    { label: 'Accuracy & technical soundness', key: 'accuracy' },
    { label: 'Cultural & contextual appropriateness', key: 'cultural' },
    { label: 'Reliability & consistency', key: 'reliability' },
    { label: 'Farmer trust', key: 'trust' },
    { label: 'Safety of recommendations', key: 'safety' },
    { label: 'Timeliness / responsiveness', key: 'timeliness' },
    { label: 'Handles follow-up questions', key: 'followup' },
  ];
  const perfCols = rows[0] ? Object.keys(rows[0]).filter(k => k.toLowerCase().includes('overall perceived performance')) : [];
  return dimensions.map((d, i) => {
    const col = perfCols[i];
    const vals = rows.map(r => parseFloat(col ? r[col] : 0)).filter(v => !isNaN(v) && v > 0);
    return { label: d.label, avg: vals.length ? avg(vals) : 0 };
  });
}

export function computePolicies(rows) {
  const policies = [
    'Kenya Data Protection Act (KDPA)',
    'Kenya AI Strategy',
    'Digital Agriculture Policy',
    'National Digital Master Plan',
    'Kenya Agricultural Sector Policy',
  ];
  const policyCols = rows[0] ? Object.keys(rows[0]).filter(k => k.toLowerCase().includes('to what extent')) : [];
  return policies.map((name, i) => {
    const col = policyCols[i];
    const dist = {};
    rows.forEach(r => {
      const v = col ? r[col] : '';
      if (v) dist[v] = (dist[v] || 0) + 1;
    });
    return { name, dist };
  });
}

export function generateLocalInsights(rows) {
  const m = computeMetrics(rows);
  const pctAI = Math.round(m.hasAI / m.n * 100);
  const pctKB = Math.round(m.hasKB / m.n * 100);
  const pctOutcomes = Math.round(m.hasOutcomes / m.n * 100);
  const pctCost = Math.round(m.costTracked / m.n * 100);
  const pctAPI = Math.round(m.hasAPI / m.n * 100);
  const accuracy = countValues(rows, ['estimated response accuracy']);
  const highAcc = (accuracy['Above 90%'] || 0) + (accuracy['91 - 100%'] || 0) + (accuracy['71 - 90%'] || 0);
  const pctHighAcc = Math.round(highAcc / m.n * 100);

  const constraints = countValues(rows, ['infrastructure constraints']);
  const topConstraint = topN(constraints, 1)[0];

  const channels = countValues(rows, ['delivery channels']);
  const topChannel = topN(channels, 1)[0];

  const languages = countValues(rows, ['languages used for advisory']);
  const langCount = Object.keys(languages).length;

  const insights = [];

  // 1. Market Opportunity — AI Adoption
  if (pctAI < 80) {
    insights.push({ type: 'GROWTH OPPORTUNITY', title: `${100 - pctAI}% Untapped Market for AI Advisory`, text: `Only ${m.hasAI} of ${m.n} organizations currently offer AI-powered advisory. The remaining ${m.n - m.hasAI} represent an addressable market for onboarding partnerships. Recommendation: Launch a pilot enablement program targeting non-AI orgs to expand ecosystem reach and drive platform adoption revenue.` });
  } else {
    insights.push({ type: 'COMPETITIVE EDGE', title: `${pctAI}% AI Adoption — Market Leader Position`, text: `${m.hasAI} of ${m.n} organizations already offer AI advisory. With high adoption, the competitive advantage shifts to quality differentiation. Recommendation: Invest in premium accuracy benchmarking and certification programs to capture upsell revenue.` });
  }

  // 2. Revenue Risk — Accuracy Gap
  insights.push({ type: pctHighAcc < 70 ? 'CRITICAL RISK' : 'STRATEGIC PRIORITY', title: 'Accuracy Gap Threatens Retention', text: `${pctHighAcc}% report 71%+ accuracy vs. the 92.7% Farmer.Chat benchmark. ${pctHighAcc < 70 ? 'Low accuracy drives farmer churn and erodes trust. Estimated revenue at risk: organizations below benchmark face 2-3x higher drop-off rates. Recommendation: Allocate budget for RAGAS evaluation frameworks and content verification pipelines — expected ROI within 2 quarters.' : 'Good foundation but closing the gap to 92.7% is key for competitive positioning. Recommendation: Invest in automated quality scoring to maintain and improve accuracy systematically.'}` });

  // 3. Scalability Blocker — API Readiness
  if (pctAPI < 50) {
    insights.push({ type: 'CRITICAL RISK', title: 'Low API Readiness Blocks Scale', text: `Only ${pctAPI}% (${m.hasAPI}/${m.n}) have API capability. Without APIs, organizations cannot integrate with government systems, payment platforms, or other advisory networks — limiting total addressable market. Recommendation: Prioritize API-first architecture; each integration partner can unlock 10-50K additional farmers.` });
  } else {
    insights.push({ type: 'GROWTH OPPORTUNITY', title: 'API Readiness Enables Partnerships', text: `${pctAPI}% have API capability — a strong foundation for ecosystem partnerships. Recommendation: Establish a shared integration marketplace and revenue-sharing model with delivery partners to accelerate network effects.` });
  }

  // 4. Impact Measurement — Outcome Tracking
  if (pctOutcomes < 60) {
    insights.push({ type: 'INVESTMENT NEEDED', title: 'No Outcome Data = No Funding Case', text: `Only ${pctOutcomes}% track farmer-level outcomes. Donors and investors require measurable impact evidence. Without it, securing Series A or next-round grant funding becomes significantly harder. Recommendation: Deploy standardized outcome tracking (yield, income, adoption) aligned to 60dB methodology — this directly de-risks future fundraising.` });
  } else {
    insights.push({ type: 'STRATEGIC PRIORITY', title: 'Leverage Outcome Data for Funding', text: `${pctOutcomes}% already track farmer outcomes — a differentiator for grant applications and investor pitches. Recommendation: Aggregate cross-org impact data into a sector-level report to attract larger institutional funders and demonstrate ecosystem-wide ROI.` });
  }

  // 5. Unit Economics — Cost Tracking
  if (pctCost < 50) {
    insights.push({ type: 'INVESTMENT NEEDED', title: 'Unknown Unit Economics Risk', text: `Only ${pctCost}% track delivery costs. Without cost-per-farmer and cost-per-query data, pricing models are guesswork and margins are invisible. Recommendation: Implement cost instrumentation across all delivery channels — target a cost-per-farmer-interaction benchmark to inform sustainable pricing within 1 quarter.` });
  } else {
    insights.push({ type: 'COMPETITIVE EDGE', title: 'Cost Data Enables Pricing Power', text: `${pctCost}% track costs — use this to establish industry-leading unit economics. Recommendation: Publish anonymized cost benchmarks to position as thought leader and attract cost-conscious partners looking for proven delivery models.` });
  }

  // 6. Operational Risk — Top Infrastructure Constraint
  if (topConstraint) {
    insights.push({ type: 'CRITICAL RISK', title: `#1 Blocker: ${topConstraint[0].slice(0, 35)}`, text: `"${topConstraint[0]}" affects ${topConstraint[1]} of ${m.n} organizations (${Math.round(topConstraint[1]/m.n*100)}%). This is the single largest operational bottleneck limiting service delivery and farmer reach. Recommendation: Allocate dedicated budget to mitigate this constraint — every percentage point improvement here directly increases farmer engagement capacity.` });
  }

  // 7. Content Moat — Knowledge Base
  if (pctKB < 60) {
    insights.push({ type: 'STRATEGIC PRIORITY', title: 'Build Content Moat via Knowledge Base', text: `Only ${pctKB}% use structured knowledge bases. Curated, verified agricultural content is a defensible competitive advantage that competitors cannot easily replicate. Recommendation: Invest in building a shared knowledge repository targeting 10,000 verified entries — this becomes the ecosystem's core IP and a barrier to entry for new players.` });
  } else {
    insights.push({ type: 'COMPETITIVE EDGE', title: 'Knowledge Base as Strategic Asset', text: `${pctKB}% use knowledge bases — a strong content foundation. Recommendation: Monetize this through content licensing, cross-org knowledge sharing agreements, and premium verified-content tiers to create a new revenue stream.` });
  }

  // 8. Growth Metric — Total Farmer Reach
  insights.push({ type: 'GROWTH OPPORTUNITY', title: `${m.totalFarmers.toLocaleString()} Farmers — Scale Path`, text: `Current reach: ${m.totalFarmers.toLocaleString()} farmers across ${m.n} organizations via ${topChannel ? topChannel[0] : 'mobile'} (${langCount} languages). ${m.totalFarmers < 100000 ? `This is 17% of the Farmer.Chat benchmark (565K). To hit scale, prioritize the top-performing delivery channel and expand multilingual support — each new language can unlock 15-25% more farmers in underserved regions.` : `Strong cumulative reach. Recommendation: Shift KPIs from farmer count to engagement depth (repeat usage, advisory-to-action conversion rate) to demonstrate higher per-farmer value to investors.`}` });

  return insights;
}

export function buildInsightPrompt(rows) {
  const m = computeMetrics(rows);
  const summary = {
    n: m.n,
    orgTypes: countValues(rows, ['organization type']),
    hasAI: m.hasAI,
    channels: countValues(rows, ['delivery channels']),
    llms: countValues(rows, ['large language model']),
    accuracy: countValues(rows, ['estimated response accuracy']),
    constraints: countValues(rows, ['infrastructure constraints']),
    evalFrameworks: countValues(rows, ['ai evaluation framework']),
    outcomes: countValues(rows, ['outcome evidence']),
    hasKB: m.hasKB,
    costTracked: m.costTracked,
    apiReady: m.hasAPI,
    multilingual: countValues(rows, ['multilingual delivery']),
    maturity: countValues(rows, ['maturity stage']),
    sustainability: countValues(rows, ['sustainability model']),
    totalFarmers: m.totalFarmers,
  };

  return `You are a strategic business advisor analyzing a Kenya AI-Enabled Agricultural Advisory Services baseline survey for executive decision-makers.

Survey data summary (${summary.n} respondents):
- Organization types: ${JSON.stringify(summary.orgTypes)}
- Offer AI advisory: ${summary.hasAI}/${summary.n}
- Delivery channels: ${JSON.stringify(summary.channels)}
- LLMs in use: ${JSON.stringify(summary.llms)}
- Response accuracy rates: ${JSON.stringify(summary.accuracy)}
- Infrastructure constraints: ${JSON.stringify(summary.constraints)}
- AI evaluation frameworks: ${JSON.stringify(summary.evalFrameworks)}
- Outcome evidence: ${JSON.stringify(summary.outcomes)}
- Have knowledge base: ${summary.hasKB}/${summary.n}
- Track costs: ${summary.costTracked}/${summary.n}
- API capability: ${summary.apiReady}/${summary.n}
- Multilingual approach: ${JSON.stringify(summary.multilingual)}
- System maturity: ${JSON.stringify(summary.maturity)}
- Sustainability models: ${JSON.stringify(summary.sustainability)}
- Total farmers reached (cumulative): ${summary.totalFarmers.toLocaleString()}

Benchmarks:
- 60dB AIEP: NPS target >50, female users ≥40%, info "always" available 72%
- Farmer.Chat: 92.7% accuracy, 565K users
- Kenya AIEP target: 10,000 verified content entries

Generate exactly 8 actionable business insights as a JSON array. Each block has:
- type: "CRITICAL RISK" | "GROWTH OPPORTUNITY" | "STRATEGIC PRIORITY" | "INVESTMENT NEEDED" | "COMPETITIVE EDGE"
- title: short title (max 8 words)
- text: 2–3 sentences written for C-suite/board audience. Include specific numbers from the data. End each with a concrete "Recommendation:" that ties to revenue, cost, risk mitigation, or competitive positioning.

Focus areas: market sizing & growth levers, unit economics & pricing, revenue risks & churn drivers, partnership/integration opportunities, funding & investor readiness, operational bottlenecks, defensible competitive advantages, scalability blockers.

Respond with ONLY a valid JSON array, no markdown, no preamble.`;
}
